import React, { FC, useState } from 'react';
import { Row, Col, Button, Input, Alert, Form, Spin, Collapse, Layout } from 'antd';
import { withRouter, RouteComponentProps, Redirect } from 'react-router';
import { InfoCircleOutlined } from '@ant-design/icons';
import useAuth from './auth/use-auth';

import axios, { AxiosRequestConfig } from 'axios';

const { Header, Content, Footer } = Layout;
const { Panel } = Collapse;

const APP_VERSION = process.env.REACT_APP_API_VERSION;

const Login: FC<RouteComponentProps | any> = (props) => {
  const [userid, setUserID] = useState<string | undefined>(undefined);
  const [password, setPassword] = useState<string | undefined>(undefined);
  const [isProcessing, setIsProcessing] = useState(false);
  const [loginFailed, setLoginFailed] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);

  const { setAuthToken, logout } = useAuth();

  const handleLogin = async () => {
    setIsProcessing(true);
    const headers: any = {
      AppVersion: APP_VERSION,
      'Content-Type': 'application/json',
      accept: 'application/json',
    } as AxiosRequestConfig;
    //api only allows this malformed JSON, actual JSON object does not seem to work
    const data = `{UserID:'${userid}', Password:'${password}'}`;

    axios({
      url: '/authentication/verifypassword',
      baseURL: process.env.REACT_APP_PROXY_URL,
      method: 'post',
      headers: headers,
      data: data,
      withCredentials: true,
    })
      .then((result: any) => {
        logout();
        sessionStorage.setItem('VerifToken', result?.headers?.veriftoken);
        setAuthToken(result?.headers?.veriftoken);

        props.history.push('/uploadCredential');
      })
      .catch((exception: any) => {
        console.log('exception: ', exception);
        setErrorMessage(exception?.response?.data?.Message);
        setIsProcessing(false);
        setLoginFailed(true);
      });
  };

  const onUsernameChange = (username: string) => {
    const strippedUserId = username.replace(/\D/g, '').trim();
    form.setFieldsValue({ login: strippedUserId });
    setUserID(strippedUserId);
    setErrorMessage(undefined);
    setLoginFailed(false);
  };

  const onPasswordChange = (password: string) => {
    setErrorMessage(undefined);
    setPassword(password);
    setLoginFailed(false);
  };
  const [form] = Form.useForm();

  return (
    <>
      <Row>
        <Col span={24}>
          <h1 className="title is-4" style={{ textAlign: 'left', marginBottom: '0' }}>
            ECFMG® | FAIMER®
          </h1>
          <h3 style={{ paddingBottom: '15px' }}>Credentials Upload Tool</h3>
        </Col>
      </Row>
      <Row>
        <Col xs={24} md={12} lg={8}>
          <div>
            <div>
              <h4 style={{ paddingBottom: '15px' }}>Please log in using your ECFMG/USMLE ID and On-line Services password.</h4>
              {loginFailed ? <Alert message={errorMessage} type="error"></Alert> : <div></div>}

              <div style={{ textAlign: 'center' }}>
                <div className={'loginForm'}>
                  <Form form={form} onFinish={handleLogin}>
                    <Form.Item name="login" label="&nbsp;&nbsp;&nbsp;User ID" initialValue={userid} rules={[{ required: true, message: 'Enter Username' }]}>
                      <Input
                        className={'loginElement'}
                        style={{ margin: '3px' }}
                        value={userid}
                        onChange={(e) => onUsernameChange(e.target.value)}
                        onPressEnter={() => handleLogin()}
                        placeholder="ECFMG/USMLE ID"
                        allowClear
                      />
                    </Form.Item>

                    <Form.Item name="password" label="Password" rules={[{ required: true, message: 'Enter Password' }]}>
                      <Input.Password
                        className={'loginElement'}
                        style={{ margin: '3px' }}
                        value={password}
                        onChange={(e) => onPasswordChange(e.target.value)}
                        onPressEnter={() => handleLogin()}
                        allowClear
                      />
                    </Form.Item>

                    <div style={{ textAlign: 'right' }}>
                      <Button style={{ margin: '3px' }} type="primary" loading={isProcessing} htmlType="submit">
                        {isProcessing ? (
                          <>
                            <Spin size="small" style={{ paddingRight: '20px' }} /> Processing...
                          </>
                        ) : (
                          <>Login</>
                        )}
                      </Button>
                    </div>
                  </Form>
                </div>
              </div>
            </div>
          </div>
        </Col>
        <Col xs={0} md={1}>
          <div
            style={{
              paddingLeft: '50%',
              marginRight: '50%',
              minHeight: '100%',
              borderRight: '1px solid black',
            }}
          ></div>
        </Col>
        <Col xs={24} md={0}>
          <div style={{ paddingTop: '30px' }}></div>
        </Col>
        <Col xs={24} md={11} lg={15}>
          <Collapse ghost>
            <Panel
              header={
                <>
                  <InfoCircleOutlined /> Important Information
                </>
              }
              key="instructions"
            >
              <div
                style={{
                  textAlign: 'initial',
                  fontSize: '14px',
                  color: 'rgba(0, 0, 0, 1)',
                }}
              >
                <p>
                  The ECFMG Credentials Upload Tool is for applicants for ECFMG Certification who need to submit medical education credentials and related documents to meet the
                  medical education credentials requirements for ECFMG Certification. There is no cost for using the tool.
                </p>
                <p>If you already have submitted required credentials and related documents using the MyECFMG mobile app or by mail, you do not need to resubmit them.</p>
                <p>Documents submitted using the ECFMG Credentials Upload Tool must be in PDF format and must not be password protected.</p>
                <p>
                  Before submitting any documentation through this tool, you should read and become familiar with all information contained in the applicable edition of the{' '}
                  <a href="https://www.ecfmg.org/resources/publications.html" target="_blank" rel="noopener noreferrer">
                    ECFMG <i>Information Booklet.</i>
                  </a>{' '}
                  Before submitting English translations of documents, you should carefully review requirements in the section on English Translations, along with all other
                  requirements.
                </p>
                <p>Please allow 10 business days for processing after documentation is submitted. ECFMG will contact you if any additional information is needed.</p>
                <p>
                  All documents submitted to ECFMG using the ECFMG Credentials Upload Tool will become part of your permanent ECFMG record and will not be returned to you.
                  Submission of falsified or altered documentation through the ECFMG Credentials Upload Tool may result in a finding of “irregular behavior,” which can have serious
                  consequences. You should review and become familiar with ECFMG’s{' '}
                  <a href="https://www.ecfmg.org/programs/irregular-behavior.html" target="_blank" rel="noopener noreferrer">
                    Policies and Procedures Regarding Irregular Behavior
                  </a>{' '}
                  before submitting documentation through this tool.
                </p>
              </div>
            </Panel>
          </Collapse>
        </Col>
      </Row>
    </>
  );
};

export default withRouter(Login);
